/**
 * API 통신 모듈
 * 중구행복돌봄센터 백엔드 API와 통신하는 함수들을 정의합니다.
 * 모든 함수는 async/await 패턴을 사용하여 비동기 처리합니다.
 */

// API 서버의 기본 URL (개발 환경에서는 proxy 사용)
const BASE_URL = 'https://api.에스이임파워.kr/';
/**
 * 공지사항 목록을 조회하는 함수
 * @param {Object} params - 조회 옵션
 * @param {string} params.order - 정렬 기준 (기본값: "createdAt")
 * @param {number} params.offset - 시작 위치 (기본값: 0)
 * @param {number} params.limit - 조회할 개수 (기본값: 6)
 * @returns {Promise<Array>} 공지사항 배열
 */
export async function getNotices({
  order = 'createdAt',
  offset = 0,
  limit = 6,
}) {
  const str = 'home/api/notice/';

  // API 서버에 GET 요청
  const response = await fetch(`${BASE_URL}${str}`);
  // if (!response.ok) {
  //   throw new Error("데이터를 불러오는데 실패했습니다");
  // }

  // 응답을 JSON으로 파싱하여 반환
  const body = await response.json();
  return body;
}

/**
 * 일반자료실 목록을 조회하는 함수
 * @param {Object} params - 조회 옵션
 * @param {string} params.order - 정렬 기준 (기본값: "createdAt")
 * @param {number} params.offset - 시작 위치 (기본값: 0)
 * @param {number} params.limit - 조회할 개수 (기본값: 6)
 * @returns {Promise<Array>} 자료실 데이터 배열
 */
export async function getDatas({ order = 'createdAt', offset = 0, limit = 6 }) {
  const str = 'home/api/archive/';

  // API 서버에 GET 요청
  const response = await fetch(`${BASE_URL}${str}`);
  // if (!response.ok) {
  //   throw new Error("데이터를 불러오는데 실패했습니다");
  // }

  // 응답을 JSON으로 파싱하여 반환
  const body = await response.json();
  return body;
}

/**
 * 포토갤러리 목록을 조회하는 함수
 * @param {Object} params - 조회 옵션
 * @param {string} params.order - 정렬 기준 (기본값: "createdAt")
 * @param {number} params.offset - 시작 위치 (기본값: 0)
 * @param {number} params.limit - 조회할 개수 (기본값: 6)
 * @returns {Promise<Array>} 포토갤러리 데이터 배열
 */
export async function getPhotos({
  order = 'createdAt',
  offset = 0,
  limit = 6,
}) {
  const str = 'home/api/poto_gallery/';

  // API 서버에 GET 요청
  const response = await fetch(`${BASE_URL}${str}`);
  // if (!response.ok) {
  //   throw new Error("데이터를 불러오는데 실패했습니다");
  // }

  // 응답을 JSON으로 파싱하여 반환
  const body = await response.json();
  return body;
}

/**
 * 조직안내 이미지를 조회하는 함수
 * 조직도 이미지 URL 정보를 가져옵니다.
 * @returns {Promise<Object>} 조직도 이미지 정보 객체
 */
export async function getOrganization() {
  // API 엔드포인트 경로
  const str = 'home/api/orgnizational_guidance/';

  // API 서버에 GET 요청
  const response = await fetch(`${BASE_URL}${str}`);

  // 응답을 JSON으로 파싱하여 반환
  const body = await response.json();
  return body;
}

/**
 * 오시는 길 정보를 조회하는 함수
 * 주소, 전화번호, 지하철 정보, 버스 정보를 가져와서 가공합니다.
 * @returns {Promise<Object>} 교통 정보 객체 { address, phoneNumber, subway: [], bus: [] }
 */
export async function getDirections() {
  const str = 'home/api/directions_get/';

  // API 서버에 GET 요청
  const response = await fetch(`${BASE_URL}${str}`);
  const data = await response.json();

  // 첫 번째 데이터에서 필요한 정보 추출
  const { address, phoneNumber, subway, bus } = data[0];

  // 지하철과 버스 정보를 & 기준으로 분할하여 배열로 저장
  // 예: "2호선 을지로3가역&3호선 을지로3가역" -> ["2호선 을지로3가역", "3호선 을지로3가역"]
  const subwayArr = subway.split('&');
  const busArr = bus.split('&');

  // 분할된 정보를 객체로 반환
  return {
    address,
    phoneNumber,
    subway: subwayArr,
    bus: busArr,
  };
}

/**
 * 포토갤러리 조회수를 업데이트하는 함수
 * 갤러리 항목을 조회할 때마다 조회수(hit)를 1 증가시킵니다.
 * @param {Object} item - 업데이트할 갤러리 항목 객체
 * @param {number} no - 갤러리 항목의 고유 번호
 * @returns {Promise<Object>} 업데이트된 갤러리 항목 객체
 */
export async function update(item, no) {
  const str = 'home/api/poto_gallery/';

  // 깊은 복사를 통해 원본 데이터 보호
  const datas = JSON.parse(JSON.stringify(item));

  // 조회수를 1 증가
  const hit = datas.hit + 1;
  datas.hit = hit;

  // PUT 요청에 불필요한 파일 정보 제거
  // 파일 정보는 서버에서 관리하므로 업데이트 시 전송하지 않음
  delete datas['files'];
  delete datas['thumb_files'];

  // API 서버에 PUT 요청으로 조회수 업데이트
  const response = await fetch(`${BASE_URL}${str}${no}`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json, text/plain',
      'Content-Type': 'application/json;charset=UTF-8',
    },
    body: JSON.stringify(datas),
  });
  // if (!response.ok) {
  //   throw new Error("데이터를 수정하는데 실패했습니다");
  // }

  // 업데이트된 데이터를 반환
  const body = await response.json();
  return body;
}
